import { Box, ListItem, Text, UnorderedList } from '@chakra-ui/react'
import React from 'react'

// Helper function to format field names from camelCase/snake_case to readable text
const formatFieldName = (fieldName: string): string => {
  // Handle common patterns
  if (fieldName.includes('_')) {
    // snake_case to Title Case
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  // camelCase to Title Case
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim()
}

interface FormErrorDisplayProps {
  errors: Record<string, any>
  fieldNameMap?: Record<string, string>
  title?: string
  showTitle?: boolean
}

// Helper function to convert technical validation errors to user-friendly messages
const getUserFriendlyErrorMessage = (
  fieldName: string,
  error: string,
  fieldNameMap: Record<string, string> = {}
): string => {
  // Use custom field name mapping if provided, otherwise format the field name
  const friendlyFieldName =
    fieldNameMap[fieldName] || formatFieldName(fieldName)

  // Handle specific error patterns
  if (error.includes('must be a `object` type') && error.includes('null')) {
    return `${friendlyFieldName} is required`
  }
  if (error.includes('must be a `string` type')) {
    return `${friendlyFieldName} is required`
  }
  if (error.includes('must be a `number` type')) {
    return `${friendlyFieldName} must be a valid number`
  }
  if (error.includes('must be a `boolean` type')) {
    return `${friendlyFieldName} is required`
  }
  if (error.includes('required')) {
    return `${friendlyFieldName} is required`
  }
  if (error.includes('must be a valid email')) {
    return `${friendlyFieldName} must be a valid email address`
  }
  if (error.includes('must be at least')) {
    const match = error.match(/must be at least (\d+) characters/)
    if (match) {
      return `${friendlyFieldName} must be at least ${match[1]} characters`
    }
  }
  if (error.includes('must be no more than')) {
    const match = error.match(/must be no more than (\d+) characters/)
    if (match) {
      return `${friendlyFieldName} must be no more than ${match[1]} characters`
    }
  }
  if (error.includes('must be one of the following values')) {
    return `${friendlyFieldName} must be a valid option`
  }

  // Default fallback - clean up the error message
  return error.replace(/must be a `[^`]+` type/, 'is required')
}

export const FormErrorDisplay: React.FC<FormErrorDisplayProps> = ({
  errors,
  fieldNameMap = {},
  title = 'Please fix the following errors:',
  showTitle = true
}) => {
  if (Object.values(errors).length === 0) {
    return null
  }

  return (
    <Box color="red.500" fontSize="sm">
      {showTitle && <Text mb={2}>{title}</Text>}
      <UnorderedList>
        {Object.entries(errors).map(([fieldName, error], idx) => (
          <ListItem key={idx} color="red.500">
            {getUserFriendlyErrorMessage(
              fieldName,
              error as string,
              fieldNameMap
            )}
          </ListItem>
        ))}
      </UnorderedList>
    </Box>
  )
}
